import React from 'react'
import clsx from 'clsx'

import TypingIndicator from '../../../../../atoms/TypingIndicator'
import Tooltip from '../../../../../atoms/Tooltip'

import {
  getShortenedDistance,
  findFilterID,
  getLatestEvent,
  formatLastMessage,
  loadTaggedUsers
} from '../../../../../../utilities'
import { useEnvironment } from '../../../../../../providers/EnvironmentProvider'
import { useUserAPI } from '../../../../../../providers/UserAPIProvider'
import { useTyping } from '../../../../../../providers/TypingProvider'
import { useAgent } from '../../../../../../providers/AgentProvider'
import { useBreakpoint } from '../../../../../../providers/BreakpointProvider'
import useInterval from '../../../../../../hooks/useInterval'
import useBaseStyles from '../../../../BaseConversation/Content/styles'
import useStyles from './styles'

const DELETED = 'Deleted Contact'

const Content = ({ name, conversation, modal }) => {
  const { user } = useEnvironment()
  const { getUsers, allUsers, loadUser } = useUserAPI()
  const { getTyping } = useTyping()
  const { currentUser } = useAgent()
  const { down767 } = useBreakpoint()

  const { session, owner_id, users } = conversation

  const allAgents = React.useMemo(
    () => Object.values(getUsers(false)),
    [getUsers]
  )

  const [timeSince, setTimeSince] = React.useState('')
  const [lastLoaded, setLastLoaded] = React.useState('')
  const nameRef = React.useRef()

  // Check last event, can be message | note | null
  const lastMessage = React.useMemo(() => {
    const filter = findFilterID(owner_id, false, session)
    const latest = getLatestEvent(conversation, filter)
    return latest
  }, [owner_id, session, conversation])

  // Updates time since every 5 seconds
  const updateTimeSince = React.useCallback(() => {
    setTimeSince(getShortenedDistance(lastMessage?.date))
  }, [lastMessage])
  useInterval(1000 * 5, updateTimeSince)

  // Gets list of typing people
  const typingNames = React.useMemo(
    () => getTyping(session),
    [getTyping, session]
  )

  const baseClasses = useBaseStyles()
  const classes = useStyles({ modal })

  const lastText = React.useMemo(
    () => formatLastMessage(lastMessage, allAgents, allUsers, currentUser),
    [lastMessage, allAgents, allUsers, currentUser]
  )

  // Loads all tagged users from a message
  React.useEffect(() => {
    if (
      !lastMessage ||
      lastMessage.content === lastLoaded ||
      typeof lastMessage.content !== 'string'
    ) {
      return
    }

    const { content = '' } = lastMessage
    loadTaggedUsers(content, loadUser)

    setLastLoaded(content)
  }, [lastMessage, lastLoaded, loadUser])

  let nameElem = (
    <div
      ref={nameRef}
      className={clsx(
        user?.deleted_at && baseClasses.faded,
        classes.name,
        modal && !down767 && classes.modalName
      )}
    >
      {user?.deleted_at ? DELETED : name}
    </div>
  )

  // Show tooltip if name is overflowing
  if (nameRef.current) {
    const element = nameRef.current
    if (element.offsetWidth < element.scrollWidth) {
      nameElem = (
        <Tooltip
          title={user?.deleted_at ? DELETED : name}
          placement='right'
          arrow
        >
          {nameElem ?? 'Loading...'}
        </Tooltip>
      )
    }
  }

  return (
    <div className={clsx(baseClasses.root, classes.root)}>
      <div className={clsx(baseClasses.contentBlock, classes.contentBlock)}>
        <div className={clsx(classes.title)}>{nameElem}</div>
        <div
          className={clsx(
            baseClasses.content,
            classes.content,
            modal && !down767 && classes.modalContent
          )}
        >
          {typingNames.length > 0 ? (
            <TypingIndicator names={typingNames} numUsers={users.length} />
          ) : (
            lastText
          )}
        </div>
      </div>

      <div className={classes.controlsBlock}>
        <div className={classes.time}>{timeSince}</div>
      </div>
    </div>
  )
}

export default Content
